import { useState, type FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';

interface ConfirmSignUpFormProps {
  readonly email: string;
  readonly onSuccess: () => void;
}

export const ConfirmSignUpForm = ({ email, onSuccess }: ConfirmSignUpFormProps) => {
  const { confirmSignUp } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await confirmSignUp(email, code);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Confirmation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <h2
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Verify Email
      </h2>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
        We sent a verification code to{' '}
        <span style={{ fontWeight: 'var(--font-weight-medium)' }}>{email}</span>.
      </p>

      {error && (
        <div
          role="alert"
          style={{
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--color-error-50)',
            padding: 'var(--space-3)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-error-700)',
          }}
        >
          {error}
        </div>
      )}

      <div>
        <label
          htmlFor="code"
          style={{
            display: 'block',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-secondary)',
          }}
        >
          Verification Code
        </label>
        <input
          id="code"
          type="text"
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="focus-ring"
          style={{
            marginTop: 'var(--space-1)',
            display: 'block',
            width: '100%',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-default)',
            padding: 'var(--space-2) var(--space-3)',
            minHeight: '44px',
            fontSize: 'var(--text-base)',
            backgroundColor: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            outline: 'none',
            transition: 'border-color var(--duration-fast) var(--ease-out)',
          }}
          placeholder="123456"
          autoComplete="one-time-code"
          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--border-focus)'; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; }}
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="focus-ring"
        style={{
          width: '100%',
          borderRadius: 'var(--radius-md)',
          backgroundColor: 'var(--color-primary-600)',
          padding: 'var(--space-3) var(--space-4)',
          minHeight: '44px',
          color: 'var(--text-inverse)',
          fontWeight: 'var(--font-weight-medium)',
          border: 'none',
          cursor: isSubmitting ? 'not-allowed' : 'pointer',
          opacity: isSubmitting ? 0.5 : 1,
          transition: 'background-color var(--duration-fast) var(--ease-out)',
        }}
        onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = 'var(--color-primary-500)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-primary-600)'; }}
      >
        {isSubmitting ? 'Verifying...' : 'Verify Email'}
      </button>
    </form>
  );
};
